import { unstable_batchedUpdates } from 'react-dom';
import dotNotationObjectUpdate from '../utils/data/dotNotationObjectUpdate';
import dotNotationMapper from '../utils/data/dotNotationMapper';

// appId → { data: {}, subscribers: Map<dotNotationPath, Set<callback>>, wildcardSubscribers: Set<callback>, _notifying, _pending }
const appDataStores = {};

// Creates an empty store if one doesn't exist yet — lets subscribers register regardless of mount/init order.
function _ensureStore(appId) {
    if (!appDataStores[appId]) {
        appDataStores[appId] = {
            data: {},
            subscribers: new Map(),
            wildcardSubscribers: new Set(),
            _notifying: false,
            _pending: null
        };
    }
    return appDataStores[appId];
}

export function initAppData(appId, initialData = {}) {
    if (typeof window === 'undefined') return; // SSR: store is client-only
    // _ensureStore covers both phases: brand-new store (no subscribers yet, notify is a no-op)
    // and a store pre-created by an early subscriber (preserves its subscribers, notifies the diff)
    const store = _ensureStore(appId);
    const prev = store.data;
    store.data = initialData;
    store._notifying = false;
    store._pending = null;
    _notifySubscribers(store, Object.keys(initialData), prev);
}

export function getAppData(appId) {
    return appDataStores[appId]?.data || {};
}

// Mirrors setViewerAppData action + SET_VIEWER_APP_DATA / SET_VIEWER_APP_SETTINGS reducer cases
export function setAppData(data = {}, appId = '', updateSettings = false, silentUpdate = false) {
    const store = appDataStores[appId];
    if (!store) return;

    const prev = store.data;

    if (updateSettings) {
        // Mirrors SET_VIEWER_APP_SETTINGS: merge data into appSettings only
        store.data = {
            ...prev,
            appSettings: {
                ...prev.appSettings,
                ...data
            }
        };
        if (!silentUpdate) {
            const changedPaths = Object.keys(data).map(k => `appSettings.${k}`); //top level only
            _notifySubscribers(store, changedPaths, prev);
        }
    } else {
        // Mirrors SET_VIEWER_APP_DATA: merge data into App_Data, appSettings always preserved
        store.data = {
            ...prev,
            ...data,
            appSettings: prev.appSettings
        };
        if (!silentUpdate) {
            _notifySubscribers(store, Object.keys(data), prev);
        }
    }
}

// Mirrors setAppviewerDataInReducer action + SET_APP_VIEWER_DATA_IN_REDUCER reducer case
export function setAppDataWithPath({ appId = '', path = '', data = {}, silentUpdate = false } = {}) {
    const store = appDataStores[appId];
    if (!store) return;

    const prev = store.data;

    if (path) {
        const updated = dotNotationObjectUpdate(prev, data, path);
        store.data = {
            ...updated,
            appSettings: prev.appSettings
        };
        if (!silentUpdate) {
            _notifySubscribers(store, [path], prev);
        }
    } else {
        // No path: same merge behaviour as SET_VIEWER_APP_DATA
        store.data = {
            ...prev,
            ...data,
            appSettings: prev.appSettings
        };
        if (!silentUpdate) {
            _notifySubscribers(store, Object.keys(data), prev);
        }
    }
}

/**
 * Subscribe to changes at specific dot-notation paths within App_Data.
 * fields must be a non-empty array — null/undefined/[] means the element
 * is static and should never re-render from data changes.
 * Returns an unsubscribe function.
 */
export function subscribeToFields(appId, fields, callback) {
    if (typeof window === 'undefined' || !callback || !fields || !fields.length) return () => {};
    const store = _ensureStore(appId);

    for (const field of fields) {
        if (!store.subscribers.has(field)) store.subscribers.set(field, new Set());
        store.subscribers.get(field).add(callback);
    }

    return () => {
        for (const field of fields) {
            const set = store.subscribers.get(field);
            if (!set) continue;
            set.delete(callback);
            if (set.size === 0) store.subscribers.delete(field);
        }
    };
}

// Subscribe to ANY App_Data change for this app — fires on every setAppData/setAppDataWithPath call.
// Returns an unsubscribe function.
export function subscribeToAny(appId, callback) {
    if (typeof window === 'undefined' || !callback) return () => {};
    const store = _ensureStore(appId);
    store.wildcardSubscribers.add(callback);
    return () => {
        store.wildcardSubscribers.delete(callback);
    };
}

// Call on app unmount to release all data and subscriptions for that app
export function clearAppData(appId) {
    delete appDataStores[appId];
}

// Notify only subscribers registered at the exact path; skip if none registered
function _addIfChanged(store, path, prev, toNotify) {
    const callbacks = store.subscribers.get(path);
    if (!callbacks || !callbacks.size) return;
    const prevVal = dotNotationMapper(prev, path);
    const newVal = dotNotationMapper(store.data, path);
    if (prevVal !== newVal) callbacks.forEach(cb => toNotify.add(cb));
}

function _dispatchNotification(store, changedPaths, prev) {
    const toNotify = new Set();

    for (const changedPath of changedPaths) {
        // 1. Exact-path subscribers — O(1) Map lookup, no iteration
        _addIfChanged(store, changedPath, prev, toNotify);

        // 2. Ancestor subscribers (e.g. 'user' when changedPath is 'user.address.city')
        //    O(depth) Map lookups
        const parts = changedPath.split('.');
        let ancestor = '';
        for (let i = 0; i < parts.length - 1; i++) {
            ancestor = i === 0 ? parts[0] : `${ancestor}.${parts[i]}`;
            _addIfChanged(store, ancestor, prev, toNotify);
        }

        // 3. Child subscribers (e.g. 'user.name' when changedPath is 'user')
        //    Unavoidable scan, but only runs for parent-level updates (setViewerAppData)
        const childPrefix = changedPath + '.';
        for (const [subscriberPath, callbacks] of store.subscribers) {
            if (subscriberPath.startsWith(childPrefix)) {
                const prevVal = dotNotationMapper(prev, subscriberPath);
                const newVal = dotNotationMapper(store.data, subscriberPath);
                if (prevVal !== newVal) callbacks.forEach(cb => toNotify.add(cb));
            }
        }
    }

    // Wildcard: subscribers that want any change
    if (store.wildcardSubscribers.size) {
        store.wildcardSubscribers.forEach(cb => toNotify.add(cb));
    }

    if (toNotify.size > 0) {
        unstable_batchedUpdates(() => {
            toNotify.forEach(cb => {
                try { cb(store.data, prev); } catch(e) { console.error('appDataStore subscriber error', e); }
            });
        });
    }
}

function _notifySubscribers(store, changedPaths, prev) {
    // Re-entrancy guard: queue any writes that happen during notification
    if (store._notifying) {
        if (!store._pending) store._pending = [];
        store._pending.push({ paths: changedPaths, prev });
        return;
    }
    store._notifying = true;
    try {
        _dispatchNotification(store, changedPaths, prev);
    } finally {
        store._notifying = false;
    }
    // Flush queued writes iteratively — never recursively
    while (store._pending && store._pending.length) {
        const batch = store._pending;
        store._pending = null;
        store._notifying = true;
        try {
            for (const { paths, prev: p } of batch) _dispatchNotification(store, paths, p);
        } finally {
            store._notifying = false;
        }
    }
}
